// The admin bot leaderboard's data (admin-only; see the edge-server's
// admin/bots-api.js). Bot rating rows are hidden from clients by RLS, so they
// come from the server, which checks the caller's own access token against the
// admin allowlist before reading anything.
import { supabase } from "./supabase";
import { dsHttpOrigin } from "../online/net";

// "all" = every level's cast in one list; a number = one crowd level.
export type BotLevelId = number | "all";

// One entry of a bot's change log: what an admin changed, and how the bot stood
// at that moment.
export interface BotChange {
  at: string;
  by: string | null;
  note: string | null;
  from: Record<string, number> | null;
  to: Record<string, number> | null; // null = back to the default
  ratingAt: number | null;
  gamesAt: number | null;
  ratingReset: boolean;
}

// One crowd-ladder row (core crowd.js), JSON-safe: null = no limit.
export interface LadderRow {
  level: number;
  minRoster: number;
  maxRoster: number | null;
  fan: number;
  replanScale: number;
  lookaheadScale: number;
  maxReevalsPerTick: number | null;
  maxFullReplansPerTick: number | null;
  personalities: boolean;
  threats: boolean;
  ownField: boolean;
  boardRefresh: number;
  tickMs: number;
}

export interface BotRow {
  agentId: string;           // the rating identity, "bot:<poolId>@L<level>"
  level: number | null;      // the one crowd level this bot lives on
  floor: boolean;            // CrashAlot: this level's floor, the worst bot we accept
  baseTraits: Record<string, number> | null; // its default settings; `traits` is what it plays with
  tuned: boolean;            // an admin changed its traits
  version: number;
  tunedAt: string | null;
  history: BotChange[];
  poolId: string;
  name: string | null; // the bot's own name (a cast member); null on the old mixed list
  inPool: boolean;
  traits: Record<string, number> | null;
  poolElo: number | null;
  poolRounds: number | null;
  poolWinRate: number | null;
  rating: number | null;
  mu: number | null;
  sigma: number | null;
  games: number;
  wins: number;
  podiums: number;
  bestPlace: number | null;
  peak: number | null;
  placePct: number | null;
  lastName: string | null;
  updatedAt: string | null;
}

export interface BotLeaderboard {
  level: BotLevelId;
  ladder: LadderRow[];
  boxes: { base: string; capacity: number }[][];
  // bot rating rows from before the casts that are still in the database (null = couldn't count)
  oldRows: number | null;
  bots: BotRow[];
}

async function adminToken(): Promise<string | null> {
  try {
    const { data } = supabase ? await supabase.auth.getSession() : { data: { session: null } };
    return data.session?.access_token ?? null;
  } catch {
    return null;
  }
}

// Tune one bot. `traits` null puts it back on its default. Applies to matches
// that start from now on. `resetRating` wipes its rating row (a number earned by
// the old settings says nothing about the new ones).
export async function saveBotSettings(input: { agentId: string; traits: Record<string, number> | null; resetRating: boolean; note: string }): Promise<{ ok?: boolean; error?: string; ratingReset?: boolean; tuned?: boolean }> {
  const token = await adminToken();
  if (!token) return { error: "not_signed_in" };
  try {
    const res = await fetch(`${dsHttpOrigin()}/api/admin/bots/settings`, {
      method: "POST",
      headers: { authorization: `Bearer ${token}`, "content-type": "application/json" },
      body: JSON.stringify(input),
    });
    return await res.json();
  } catch {
    return { error: "network" };
  }
}

// Delete the bot rating rows from before the casts (mixed-level numbers that
// nothing writes any more; they still sit on the public leaderboard).
export async function purgeOldBotRows(): Promise<{ ok?: boolean; deleted?: number | null; error?: string }> {
  const token = await adminToken();
  if (!token) return { error: "not_signed_in" };
  try {
    const res = await fetch(`${dsHttpOrigin()}/api/admin/bots/purge-old`, { method: "POST", headers: { authorization: `Bearer ${token}` } });
    return await res.json();
  } catch {
    return { error: "network" };
  }
}

// The global power-up values: what a bot's Power-up greed chases (0 = ignored,
// 1 = full pull). `defaults` come from core; `values` is what matches use now.
export interface PowerUpValues { defaults: Record<string, number>; values: Record<string, number>; retuned: boolean }
export async function fetchPowerUpValues(): Promise<PowerUpValues | { error: string }> {
  const token = await adminToken();
  if (!token) return { error: "not_signed_in" };
  try {
    const res = await fetch(`${dsHttpOrigin()}/api/admin/bots/powerups`, { headers: { authorization: `Bearer ${token}` } });
    return await res.json();
  } catch {
    return { error: "network" };
  }
}
// `values` null = back to the defaults. Applies to matches that start from now on.
export async function savePowerUpValues(values: Record<string, number> | null): Promise<(PowerUpValues & { ok?: boolean }) | { error: string }> {
  const token = await adminToken();
  if (!token) return { error: "not_signed_in" };
  try {
    const res = await fetch(`${dsHttpOrigin()}/api/admin/bots/powerups`, {
      method: "POST",
      headers: { authorization: `Bearer ${token}`, "content-type": "application/json" },
      body: JSON.stringify({ values }),
    });
    return await res.json();
  } catch {
    return { error: "network" };
  }
}

export async function fetchBotLeaderboard(level: BotLevelId): Promise<BotLeaderboard | { error: string }> {
  let token: string | null = null;
  try {
    const { data } = supabase ? await supabase.auth.getSession() : { data: { session: null } };
    token = data.session?.access_token ?? null;
  } catch { /* fall through: not signed in */ }
  if (!token) return { error: "not_signed_in" };
  try {
    const res = await fetch(`${dsHttpOrigin()}/api/admin/bots?level=${level}`, { headers: { authorization: `Bearer ${token}` } });
    return await res.json();
  } catch {
    return { error: "network" };
  }
}
